/*
 * Agrupado de las acreditaciones esperadas del día (ver
 * src/services/acreditacionesEsperadasService.js) para Control de Saldos.
 *
 * Nada de esto entra en el cálculo de saldos: es el dato que el operador
 * corrobora contra la cuenta.
 *
 * Dollar linked: se acredita en pesos al A3500. El evento trae `monto: null`, el
 * nominal en dólares y un `montoEstimado` en pesos al mayorista (lo calcula la
 * función). El estimado nunca entra al saldo: al marcarlo Acreditada el operador
 * informa el importe real (`montoReal` de la marca), que pasa a ser el `monto`.
 */

import { formatSaldo } from './formatters';

export const ESTADO_PENDIENTE = 'pendiente';
const ESTADO_ACREDITADA = 'acreditada';

/** 'YYYY-MM-DD' → 'DD/MM/AAAA'. */
export const formatIsoDay = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

export const conSigno = (monto, moneda) => `${monto >= 0 ? '+' : ''}${formatSaldo(monto, moneda)}`;

/** Evento dollar linked: nominal en dólares que se acredita en pesos. */
export const esDollarLinked = ev => ev?.monedaNominal === 'USD' && typeof ev?.montoNominal === 'number';

/**
 * Monto de un evento esperado en su moneda. Parcial (letra sin cronograma) = solo
 * el capital: lo real es al menos eso ("≥"). Dollar linked: el estimado en pesos
 * ("≈"); sin tipo de cambio, el nominal en dólares.
 */
export const formatMontoEvento = (ev, moneda) => {
  if (typeof ev.monto === 'number') return `${ev.parcial ? '≥ ' : ''}${conSigno(ev.monto, moneda)}`;
  // Espacio duro: el "≈" no se separa del importe en una columna angosta.
  if (typeof ev.montoEstimado === 'number') return `≈ ${conSigno(ev.montoEstimado, 'ARS')}`;
  if (typeof ev.montoNominal === 'number') return `${formatSaldo(ev.montoNominal, 'USD')} nominal`;
  return '—';
};

// El A3500 se publica con hasta 4 decimales (p. ej. $ 1.538,741): no se redondea a 2.
const formatA3500 = valor => `$ ${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(valor)}`;

/**
 * Aclaración de un dollar linked para mostrar debajo del monto: el nominal y de
 * dónde sale el importe en pesos. null si el evento no es linked.
 *
 * Tipo de cambio (`tipoCambioEstimado.fuente`, lo pone la función): `a3500_fixing`,
 * el A3500 del fixing de un bono, ya publicado; `a3500`, el último A3500 publicado
 * (fixing futuro); `mayorista`, pagarés MAV y bonos sin serie del BCRA.
 */
export const detalleDollarLinked = (ev) => {
  if (!esDollarLinked(ev)) return null;
  const nominal = `${formatSaldo(Math.abs(ev.montoNominal), 'USD')} nominal`;
  if (ev.montoInformado) return `${nominal} · importe acreditado informado por ${ev.marcadoPor || 'el operador'}`;
  const tc = ev.tipoCambioEstimado;
  if (typeof ev.montoEstimado === 'number' && tc?.valor) {
    const fecha = formatIsoDay(tc.fecha);
    if (tc.fuente === 'a3500_fixing') {
      return `${nominal} · al A3500 del fixing del ${fecha} (${formatA3500(tc.valor)}), 3 días hábiles antes del pago`;
    }
    if (tc.fuente === 'a3500') {
      return `${nominal} · estimado al último A3500 publicado, del ${fecha} (${formatA3500(tc.valor)}); `
        + 'se acredita al A3500 del fixing, 3 días hábiles antes del pago';
    }
    return `${nominal} · estimado al dólar mayorista del ${fecha} (${formatSaldo(tc.valor, 'ARS')}); `
      + 'se acredita al A3500 del día de pago';
  }
  return `${nominal} · se acredita en pesos al A3500 del día de pago (sin cotización para estimarlo)`;
};

/**
 * Une los documentos del día con las marcas del operador.
 *
 * @returns {Map<string, {nroComitente, nombre, fechaCartera, eventos: Array}>}
 *   cada evento lleva `estado`, `marcadoPor` y `marcadoAt` de su marca (o
 *   `pendiente` si nadie lo marcó). Un dollar linked marcado Acreditada con
 *   importe real lo trae como `monto` (y `montoInformado: true`).
 */
export const mergeAcreditaciones = (docs = [], controles = []) => {
  const marcas = new Map(controles.map(c => [c.eventId || c.id, c]));
  const porComitente = new Map();
  docs.forEach((d) => {
    const nroComitente = String(d.nroComitente || '');
    if (!nroComitente) return;
    const eventos = (Array.isArray(d.eventos) ? d.eventos : []).map((ev) => {
      const marca = marcas.get(ev.eventId);
      const montoReal = typeof ev.monto !== 'number' && marca?.estado === ESTADO_ACREDITADA
        && typeof marca.montoReal === 'number' ? marca.montoReal : null;
      return {
        ...ev,
        ...(montoReal !== null ? { monto: montoReal, montoInformado: true } : {}),
        estado: marca?.estado || ESTADO_PENDIENTE,
        marcadoPor: marca?.marcadoPor || null,
        marcadoAt: marca?.marcadoAt || null,
      };
    });
    porComitente.set(nroComitente, {
      nroComitente,
      nombre: d.nombre || '',
      fechaCartera: d.fechaCartera || null,
      eventos,
    });
  });
  return porComitente;
};

/**
 * Totales de un grupo de eventos: total (solo eventos con monto), si hay eventos sin
 * monto (dollar linked) y si alguno es parcial (letra sin cronograma: solo el
 * capital, el total es un piso). Los linked sin monto suman aparte su estimado en
 * pesos (`estimadoLinked`); `linkedSinEstimar` cuenta los que no tienen ni eso.
 */
const totalesDeEventos = eventos => ({
  total: eventos.reduce((acc, ev) => acc + (typeof ev.monto === 'number' ? ev.monto : 0), 0),
  haySinMonto: eventos.some(ev => typeof ev.monto !== 'number'),
  estimadoLinked: eventos.reduce(
    (acc, ev) => acc + (typeof ev.monto !== 'number' && typeof ev.montoEstimado === 'number' ? ev.montoEstimado : 0),
    0,
  ),
  linkedSinEstimar: eventos.filter(ev => typeof ev.monto !== 'number' && typeof ev.montoEstimado !== 'number').length,
  hayParcial: eventos.some(ev => ev.parcial === true),
});

/**
 * Resumen por comitente de una moneda: los totales de todos los eventos del día,
 * cuántos siguen pendientes de corroborar y, en `pendiente`, los totales de esos
 * pendientes (lo que falta resolver: lo acreditado ya está en el saldo y lo que no
 * llegó no se espera más hoy).
 *
 * `acreditadas`/`acreditado`: cuántos marcados Acreditada entraron al saldo y por
 * cuánto. Un dollar linked marcado sin importe real no entra (ver calculateSaldos),
 * así que tampoco se cuenta acá.
 */
export const resumirPorBucket = (porComitente, bucket) => {
  const resultado = new Map();
  porComitente.forEach((entry, nroComitente) => {
    const eventos = entry.eventos.filter(ev => ev.bucket === bucket);
    if (eventos.length === 0) return;
    const pendientes = eventos.filter(ev => ev.estado === ESTADO_PENDIENTE);
    const acreditados = eventos.filter(ev => ev.estado === ESTADO_ACREDITADA && typeof ev.monto === 'number' && ev.monto);
    resultado.set(nroComitente, {
      nroComitente,
      nombre: entry.nombre,
      fechaCartera: entry.fechaCartera,
      eventos,
      ...totalesDeEventos(eventos),
      pendientes: pendientes.length,
      pendiente: totalesDeEventos(pendientes),
      acreditadas: acreditados.length,
      acreditado: acreditados.reduce((acc, ev) => acc + ev.monto, 0),
    });
  });
  return resultado;
};

/** Totales de una moneda para el aviso de la pestaña. */
export const totalesDelBucket = (resumen) => {
  let eventos = 0;
  let pendientes = 0;
  resumen.forEach((r) => {
    eventos += r.eventos.length;
    pendientes += r.pendientes;
  });
  return { comitentes: resumen.size, eventos, pendientes };
};
